// services/api.js

export const API_URL = 'https://api.barbxpro.com.br/api';

// Gerenciador de Token
export const tokenManager = {
  save: (token) => {
    localStorage.setItem('token', token);
  },
  
  get: () => {
    return localStorage.getItem('token');
  },
  
  remove: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
};

// Função auxiliar para fazer requisições
const fetchAPI = async (endpoint, options = {}) => {
  const token = tokenManager.get();
  
  // Verificar se o body é FormData
  const isFormData = options.body instanceof FormData;
  
  const config = {
    ...options,
    headers: {
      // Não definir Content-Type para FormData - o navegador define automaticamente com boundary
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    },
  };

  // Se não tiver body definido nas options, não fazer nada (já foi definido no api.post)
  // Se tiver body e não for FormData, garantir que seja string JSON
  if (config.body && !isFormData && typeof config.body !== 'string') {
    config.body = JSON.stringify(config.body);
  }

  const response = await fetch(`${API_URL}${endpoint}`, config);
  
  if (!response.ok) {
    // Handle 401 Unauthorized errors - token expired or invalid
    if (response.status === 401) {
      // Clear invalid token
      tokenManager.remove();
      
      // Dispatch custom event to trigger logout (App.js will listen to this)
      window.dispatchEvent(new CustomEvent('auth:unauthorized', {
        detail: { message: 'Sessão expirada. Por favor, faça login novamente.' }
      }));
      
      // Don't throw for login/register endpoints to avoid infinite loop
      if (!endpoint.includes('/auth/login') && !endpoint.includes('/auth/register')) {
        const error = await response.json().catch(() => ({ message: 'Token inválido ou expirado' }));
        throw { 
          response: { 
            status: 401,
            data: error 
          },
          isAuthError: true
        };
      }
    }
    
    const error = await response.json().catch(() => ({ message: 'Erro na requisição' }));
    throw { 
      response: { 
        status: response.status,
        data: error 
      } 
    };
  }
  
  return response.json();
};

// API de Autenticação
export const auth = {
  login: async (email, senha) => {
    console.log('🔵 API: Enviando requisição de login para:', `${API_URL}/auth/login`);
    console.log('📤 Dados:', { email, senha: '***' });
    
    try {
      const response = await fetchAPI('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, senha }),
      });
      
      console.log('✅ API: Login bem-sucedido', response);
      return response;
    } catch (error) {
      console.error('❌ API: Erro no login', error);
      throw error;
    }
  },

  register: async (userData) => {
    return fetchAPI('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  me: async () => {
    return fetchAPI('/auth/me');
  }
};

// API de Usuários
export const users = {
  getAll: async () => {
    return fetchAPI('/usuarios');
  },

  getById: async (id) => {
    return fetchAPI(`/usuarios/${id}`);
  },

  create: async (userData) => {
    return fetchAPI('/usuarios', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  update: async (id, userData) => {
    return fetchAPI(`/usuarios/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  },

  delete: async (id) => {
    return fetchAPI(`/usuarios/${id}`, {
      method: 'DELETE',
    });
  }
};

// API de Barbeiros
export const barbeiros = {
  getAll: async () => {
    return fetchAPI('/barbeiros');
  },

  getById: async (id) => {
    return fetchAPI(`/barbeiros/${id}`);
  },

  create: async (barbeiroData) => {
    return fetchAPI('/barbeiros', {
      method: 'POST',
      body: JSON.stringify(barbeiroData),
    });
  },

  update: async (id, barbeiroData) => {
    return fetchAPI(`/barbeiros/${id}`, {
      method: 'PUT',
      body: JSON.stringify(barbeiroData),
    });
  }
};

// API de Serviços
export const servicos = {
  getAll: async () => {
    return fetchAPI('/servicos');
  },

  create: async (servicoData) => {
    return fetchAPI('/servicos', {
      method: 'POST',
      body: JSON.stringify(servicoData),
    });
  },

  update: async (id, servicoData) => {
    return fetchAPI(`/servicos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(servicoData),
    });
  }
};

// API de Comandas
export const comandas = {
  getAll: async () => {
    return fetchAPI('/comandas');
  },

  create: async (comandaData) => {
    return fetchAPI('/comandas', {
      method: 'POST',
      body: JSON.stringify(comandaData),
    });
  },

  update: async (id, comandaData) => {
    return fetchAPI(`/comandas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(comandaData),
    });
  },

  pagar: async (id, formaPagamento) => {
    return fetchAPI(`/comandas/${id}/pagar`, {
      method: 'POST',
      body: JSON.stringify({ formaPagamento }),
    });
  }
};

// API de Agendamentos
export const agendamentos = {
  getAll: async () => {
    return fetchAPI('/agendamentos');
  },

  create: async (agendamentoData) => {
    return fetchAPI('/agendamentos', {
      method: 'POST',
      body: JSON.stringify(agendamentoData),
    });
  },

  updateStatus: async (id, status) => {
    return fetchAPI(`/agendamentos/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }
};

// API de Configurações
export const configuracoes = {
  get: async () => {
    return fetchAPI('/configuracoes');
  },

  update: async (configData) => {
    return fetchAPI('/configuracoes', {
      method: 'PUT',
      body: JSON.stringify(configData),
    });
  }
};

const api = {
  auth,
  users,
  barbeiros,
  servicos,
  comandas,
  agendamentos,
  configuracoes,
  tokenManager
};

// Métodos HTTP genéricos
api.get = (endpoint, options = {}) => fetchAPI(endpoint, { method: 'GET', ...options });
api.post = (endpoint, data, options = {}) => {
  // Se data é FormData, passar diretamente; senão, fazer JSON.stringify
  const body = data instanceof FormData ? data : JSON.stringify(data);
  return fetchAPI(endpoint, { 
    method: 'POST', 
    body,
    ...options
  });
};
api.put = (endpoint, data, options = {}) => {
  const body = data instanceof FormData ? data : JSON.stringify(data);
  return fetchAPI(endpoint, { 
    method: 'PUT', 
    body,
    ...options
  });
};
api.delete = (endpoint, options = {}) => fetchAPI(endpoint, { method: 'DELETE', ...options });
api.patch = (endpoint, data, options = {}) => {
  const body = data instanceof FormData ? data : JSON.stringify(data);
  return fetchAPI(endpoint, { 
    method: 'PATCH', 
    body,
    ...options
  });
};

export default api;
